import React, { useEffect, useState } from 'react';
import { MusicNotes, ForkKnife, Airplane, BookOpen, FilmStrip } from '@phosphor-icons/react';

import Progress from '../../components/ui/progress';
import {
  EXPERIENCE_TRANSITION_MS,
  TASTE_DOMAINS,
  TASTE_INTENSITIES,
} from '../content';
import {
  COMPLETE,
  TRANSITIONING,
  advanceExperience,
  createExperienceState,
  resetExperience,
  selectExperienceOption,
} from '../state/experienceState';
import useTransitionFocus from '../hooks/useTransitionFocus';
import NarrativeChapter from './NarrativeChapter';
import ResonanceOrb from './ResonanceOrb';

const TASTE_ICONS = {
  'Music': <MusicNotes className="taste-domain-icon" size={24} />,
  'Food & Dining': <ForkKnife className="taste-domain-icon" size={24} />,
  'Travel': <Airplane className="taste-domain-icon" size={24} />,
  'Books': <BookOpen className="taste-domain-icon" size={24} />,
  'Movies & Shows': <FilmStrip className="taste-domain-icon" size={24} />,
};

/**
 * Renders the five-domain taste-weighting preview. Accepts no props.
 *
 * @returns {React.ReactElement} Chapter three with one live taste domain,
 *   three guarded intensity choices, progress, and replayable completion.
 * @sideEffects Schedules and clears one browser timeout after a valid choice,
 *   and restores focus after prompt replacement.
 */
export default function TastePreview({ isActive, index = 5 }) {
  const [state, setState] = useState(
    () => createExperienceState(TASTE_DOMAINS.length),
  );
  const [selections, setSelections] = useState({});
  const promptRegionRef = useTransitionFocus(state.phase);

  useEffect(() => {
    if (!isActive) {
      setState(createExperienceState(TASTE_DOMAINS.length));
      setSelections({});
    }
  }, [isActive]);

  useEffect(() => {
    if (state.phase !== TRANSITIONING) {
      return undefined;
    }

    const transitionTimer = window.setTimeout(() => {
      setState((latest) => advanceExperience(latest));
    }, EXPERIENCE_TRANSITION_MS);

    return () => window.clearTimeout(transitionTimer);
  }, [state.phase]);

  const handleSelect = (intensity) => {
    const domain = TASTE_DOMAINS[state.index];
    setSelections((prev) => ({ ...prev, [domain]: intensity }));
    setState((current) => selectExperienceOption(current, intensity));
  };

  const isComplete = state.phase === COMPLETE;
  const progress = (state.index / state.total) * 100;
  const isTransitioning = state.phase === TRANSITIONING;

  return (
    <NarrativeChapter
      index={index}
      id="taste"
      title="Taste has weight."
      body={isComplete ? null : 'Taste is weighted by meaning: casual, regular, or core to identity.'}
    >
      <div className="experience-preview taste-preview">
        {isComplete ? null : (
          <Progress
            value={progress}
            current={state.index}
            total={state.total}
            label="Taste"
          />
        )}
        <div
          ref={promptRegionRef}
          className={`experience-preview__prompt experience-preview__prompt--${state.phase}`}
          aria-live="polite"
          aria-atomic="true"
          tabIndex={-1}
        >
          {isComplete ? (
            <div className="experience-preview__complete">
              <div className="experience-preview__complete-content">
                <p>A hobby can fill a prompt. Taste reveals what actually shapes a life.</p>
                <ResonanceOrb iconType="taste" tasteSelections={selections} />
              </div>
            </div>
          ) : (
            <>
              <div className="taste-domain-display">
                {TASTE_ICONS[TASTE_DOMAINS[state.index]]}
                <p>{TASTE_DOMAINS[state.index]}</p>
              </div>
              <div className="experience-preview__options">
                {TASTE_INTENSITIES.map((intensity) => (
                  <button
                    key={state.index + '-' + intensity}
                    type="button"
                    data-desc={
                      intensity === 'Casual'
                        ? 'Background interest • Vibe'
                        : intensity === 'Regular'
                        ? 'Regular part of my week • Habit'
                        : 'Defining passion • Identity'
                    }
                    disabled={isTransitioning}
                    onClick={() => handleSelect(intensity)}
                  >
                    {intensity}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </NarrativeChapter>
  );
}
